import { type ReactNode, createContext, useContext } from 'react';
import { ReactKeycloakProvider, useKeycloak } from '@react-keycloak/web';
import { hubKeycloak, KEYCLOAK_INIT_OPTIONS } from '../keycloak';
import { LoadingScreen } from '../components/LoadingScreen';

interface AuthContextValue {
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: () => void;
  logout: () => void;
  userName: string | null;
  initials: string;
}

/**
 * Derives avatar initials from a user name (e.g. "jane.doe" -> "JD",
 * "alice" -> "A"). Falls back to an empty string when no name is known.
 */
function deriveInitials(userName: string | null): string {
  if (!userName) return '';
  const parts = userName.split(/[\s._-]+/).filter((part) => part.length > 0);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return `${parts[0]!.charAt(0)}${parts[parts.length - 1]!.charAt(0)}`.toUpperCase();
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Syncs ReactKeycloakProvider state into the AuthContext.
 * Same pattern as TePresta (ReactKeycloakProvider + login-required).
 */
function AuthStateSync({ children }: { children: ReactNode }) {
  const { keycloak: kc, initialized } = useKeycloak();

  const userName = kc.tokenParsed?.preferred_username ?? kc.tokenParsed?.name ?? null;

  const value: AuthContextValue = {
    isAuthenticated: !!kc.authenticated,
    isInitialized: initialized,
    login: () => kc.login(),
    logout: () => kc.logout({ redirectUri: window.location.origin + '/login' }),
    userName,
    initials: deriveInitials(userName),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <ReactKeycloakProvider
      authClient={hubKeycloak}
      initOptions={KEYCLOAK_INIT_OPTIONS}
      LoadingComponent={<LoadingScreen />}
    >
      <AuthStateSync>{children}</AuthStateSync>
    </ReactKeycloakProvider>
  );
}
